import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, CheckCircle, Clock, Loader2, CreditCard, Banknote, AlertCircle, ChevronDown, ChevronUp, History } from 'lucide-react';
import api from '../utils/api';
import { useSocket } from '../context/SocketContext';

interface DriverSummary {
  id: string;
  name: string;
  balance: number;
  phone: string;
  email?: string;
  bankDetails: {
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    branch: string;
  } | null;
  pendingRequest: {
    id: string;
    amount: number;
    status: string;
    requestedAt: string;
  } | null;
}

const PayoutManagement = () => {
  const [drivers, setDrivers] = useState<DriverSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  
  const [expandedDriverId, setExpandedDriverId] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { socket } = useSocket();

  const fetchData = async () => {
    try {
      const response = await api.get('/drivers/payout-summary');
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching driver summaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleHistory = async (driverId: string) => {
    if (expandedDriverId === driverId) {
      setExpandedDriverId(null);
      setHistory([]);
    } else {
      setExpandedDriverId(driverId);
      setLoadingHistory(true);
      try {
        const response = await api.get(`/driver/${driverId}/payouts`);
        setHistory(response.data);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoadingHistory(false);
      }
    }
  };

  useEffect(() => {
    fetchData();

    if (socket) {
      socket.on('driver_updated', fetchData);
      socket.on('driver_approved', fetchData);
      socket.on('payout_processed', fetchData);

      return () => {
        socket.off('driver_updated', fetchData);
        socket.off('driver_approved', fetchData);
        socket.off('payout_processed', fetchData);
      };
    }
  }, [socket]);

  const handleApprovePayout = async (payoutId: string, driverId: string, amount: number) => {
    if (!window.confirm(`Confirm transfer of Rs ${amount}? This will update the driver's balance.`)) return;

    setProcessingId(payoutId);
    try {
      await api.patch(`/payout/${payoutId}/approve`, { driverId, amount });
      fetchData(); // Refresh everything
    } catch (error) {
      console.error('Error approving payout:', error);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[2rem] p-12 flex flex-col items-center justify-center border border-gray-100 shadow-sm min-h-[400px]">
        <Loader2 className="w-10 h-10 text-[#05A664] animate-spin mb-4" />
        <p className="text-[#909090] font-medium">Loading driver payout status...</p>
      </div>
    );
  }

  const pendingDrivers = drivers.filter(d => d.pendingRequest);

  return (
    <div className="space-y-10">
      {/* Action Required Section */}
      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-8 flex items-center justify-between border-b border-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
              <ArrowRightLeft className="text-orange-600 w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#121415]">Action Required</h2>
              <p className="text-sm text-[#909090] font-medium">Drivers waiting for payment transfer</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-xl text-orange-600 font-bold text-sm">
            {pendingDrivers.length} Pending
          </div>
        </div>

        <div className="p-8">
          {pendingDrivers.length === 0 ? (
            <div className="text-center py-20 bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-4 mx-auto">
                <CheckCircle className="w-8 h-8 text-[#05A664]" />
              </div>
              <h3 className="text-lg font-bold text-[#121415]">All Transfers Complete</h3>
              <p className="text-sm text-[#909090] max-w-[280px] mx-auto">There are no pending payout requests from drivers at this moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {pendingDrivers.map((driver) => (
                <DriverPayoutCard 
                  key={driver.id} 
                  driver={driver} 
                  isExpanded={expandedDriverId === driver.id}
                  onToggleHistory={() => toggleHistory(driver.id)}
                  onApprove={handleApprovePayout}
                  processingId={processingId}
                  history={history}
                  loadingHistory={loadingHistory}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sub-component for clarity
const DriverPayoutCard = ({ driver, isExpanded, onToggleHistory, onApprove, processingId, history, loadingHistory }: any) => {
  const req = driver.pendingRequest;
  
  return (
    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden hover:border-[#05A664]/30 transition-all group">
      <div className="p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="flex items-start gap-6">
          <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Banknote className="text-orange-600 w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-[#909090] uppercase tracking-wider">Requested Payout</span>
              <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-bold uppercase rounded-md tracking-wider">Needs Action</span>
            </div>
            <h3 className="text-2xl font-black text-[#121415] mb-1">{driver.name}</h3>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-sm text-[#909090] font-medium">{driver.phone}</p>
              <p className="text-sm text-[#d1d5db]">•</p>
              <p className="text-sm text-[#909090] font-medium">{driver.email || 'No email'}</p>
            </div>
            <p className="text-xs text-[#909090] font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" /> Requested on {new Date(req.requestedAt).toLocaleDateString()} at {new Date(req.requestedAt).toLocaleTimeString()}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center lg:items-end justify-center px-8 py-4 bg-[#F1F8F5]/50 rounded-2xl lg:bg-transparent lg:p-0">
          <span className="text-xs font-bold text-[#909090] uppercase tracking-wider mb-1">Transfer Amount</span>
          <span className="text-4xl font-black text-[#05A664]">Rs {req.amount.toLocaleString()}</span>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch gap-4">
          <button
            onClick={onToggleHistory}
            className={`flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold text-sm border transition-all ${
              isExpanded ? 'bg-gray-100 border-gray-200 text-[#121415]' : 'border-gray-200 text-[#121415] hover:bg-gray-50'
            }`}
          >
            <History className="w-4 h-4" />
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          <button
            disabled={processingId === req.id}
            onClick={() => onApprove(req.id, driver.id, req.amount)}
            className="flex-1 lg:flex-none px-8 py-4 bg-[#121415] text-white rounded-2xl font-bold text-sm hover:bg-[#05A664] transition-all shadow-xl shadow-black/10 flex items-center justify-center gap-2"
          >
            {processingId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Complete Transfer
          </button>
        </div>
      </div>

      <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-50 flex flex-col sm:flex-row items-center gap-8">
        {driver.bankDetails ? (
          <div className="flex items-center gap-8 overflow-x-auto w-full no-scrollbar">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                <CreditCard className="w-4 h-4 text-[#909090]" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#909090] uppercase tracking-widest">Account Name</p>
                <p className="text-xs font-bold text-[#121415]">{driver.bankDetails.accountHolderName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div>
                <p className="text-[10px] font-bold text-[#909090] uppercase tracking-widest">Bank / Branch</p>
                <p className="text-xs font-bold text-[#121415]">{driver.bankDetails.bankName} - {driver.bankDetails.branch}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div>
                <p className="text-[10px] font-bold text-[#909090] uppercase tracking-widest">Account Number</p>
                <p className="text-xs font-black text-[#05A664] tracking-wider">{driver.bankDetails.accountNumber}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs font-bold tracking-tight">Warning: Bank details not configured for this driver!</span>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="px-8 pb-8 pt-4 bg-[#F1F8F5]/30 border-t border-gray-100 animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-2 mb-6 pt-4">
            <div className="w-2 h-6 bg-[#05A664] rounded-full"></div>
            <h4 className="font-bold text-[#121415]">Past Payouts for {driver.name}</h4>
          </div>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-[#05A664] animate-spin" /></div>
          ) : history.length === 0 ? (
            <p className="text-center py-6 text-sm text-[#909090] italic bg-white rounded-2xl border border-gray-100">No previous transactions found.</p>
          ) : (
            <div className="space-y-3">
              {history.map((item: any) => (
                <div key={item.id} className="p-5 bg-white rounded-2xl border border-gray-50 shadow-sm space-y-4">
                  <div className="flex justify-between items-center">
                    <span className={`text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-wider ${
                      item.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>{item.status}</span>
                    <p className="text-xl font-black text-[#121415]">Rs {item.amount.toLocaleString()}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200/50">
                    <div className="flex flex-col">
                      <span className="text-[9px] font-bold text-[#909090] uppercase tracking-widest mb-1">Requested On</span>
                      <span className="text-[11px] font-bold text-[#121415]">{new Date(item.requestedAt).toLocaleDateString()}</span>
                      <span className="text-[10px] text-[#909090]">{new Date(item.requestedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    
                    {item.status === 'approved' && (
                      <div className="flex flex-col text-right">
                        <span className="text-[9px] font-bold text-[#909090] uppercase tracking-widest mb-1">Settled On</span>
                        <span className="text-[11px] font-bold text-[#121415]">{new Date(item.paidAt).toLocaleDateString()}</span>
                        <span className="text-[10px] text-[#909090]">{new Date(item.paidAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PayoutManagement;
